import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import TLogo from '../../assets/appLogoFinalDark.png';
import lightRoad from '../../assets/lightRoad.png'

export default function SignUp() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isAccountCreated, setIsAccountCreated] = useState(false);

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const formIsValid =
    firstName.trim() &&
    lastName.trim() &&
    email.trim() &&
    password.trim() &&
    confirmPassword.trim() &&
    password === confirmPassword &&
    isValidEmail(email);

  const handleEmailBlur = async () => {
    setEmailError('');
    if (!isValidEmail(email)) {
      setEmailError('Invalid email format.');
      return;
    }

    try {
      const { data: existingUser, error: emailCheckError } = await supabase
        .from('profile') // Adjust if needed
        .select('email_address')
        .eq('email_address', email)
        .single();

      if (existingUser) {
        setEmailError('Email already exists.');
      } else if (emailCheckError && emailCheckError.code !== 'PGRST116') {
        throw emailCheckError;
      }
    } catch (err) {
      setEmailError('An unexpected error occurred while checking the email.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!isValidEmail(email)) {
      setError('Invalid email format.');
      return;
    }

    if (emailError) {
      setError(emailError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { first_name: firstName, last_name: lastName },
        },
      });

      if (error) throw error;

      const user = data.user;
      if (user) {
        const { error: profileError } = await supabase
          .from('profile') // Adjust table name if needed
          .update({ first_name: firstName, last_name: lastName })
          .eq('id', user.id);

        if (profileError) throw profileError;
      }

      setIsAccountCreated(true);
    } catch (err) {
      setError(err.message || 'An unexpected error occurred.');
    }
  };

  useEffect(() => {
    document.title = "Sign Up | TransTech Connect";
    return () => {
      document.title = "TransTech Connect";
    };
  }, []);

  if (isAccountCreated) {
    return (
      <div className="relative flex min-h-screen items-center justify-center bg-gray-50">
        {/* Diagonal background container */}
        <div
          className="absolute inset-0 bg-no-repeat bg-cover bg-center"
          style={{
            backgroundImage: {lightRoad},
            clipPath: 'polygon(0 0, 100% 0, 100% 40%, 0 100%)',
          }}
        />
        <div className="relative z-10 max-w-md w-full bg-white border rounded-lg p-8 shadow-lg">
          <div className="flex flex-col items-center mb-6">
            <img alt="Your Company" src={TLogo} className="h-10 w-auto bg-black p-2 rounded-md" />
            <h2 className="mt-4 text-center text-2xl font-bold tracking-tight text-gray-900">
              Account Created!
            </h2>
          </div>
          <p className="text-center text-sm text-gray-600">
            Login to your account to Join or Create a Company, View Measurements, and more.
          </p>
          <div className="mt-6">
            <button
              onClick={() => (window.location.href = '/')}
              className="w-full rounded-md bg-yellow-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-yellow-500"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gray-50">
      {/* Diagonal background container */}
      <div
        className="absolute inset-0 bg-no-repeat bg-cover bg-center"
        style={{
          backgroundImage: `url(${lightRoad})`,
          clipPath: 'polygon(0 0, 100% 0%, 0 71%, 0 71%)',
        }}
      />

      {/* Sign-up card */}
      <div className="relative z-10 max-w-md w-full bg-white border rounded-lg p-8 shadow-lg">
        <div className="flex flex-col items-center mb-6">
          <img alt="Your Company" src={TLogo} className="h-10 w-auto bg-black p-2 rounded-md" />
          <h2 className="mt-4 text-center text-2xl font-bold tracking-tight text-gray-900">
            Create an account
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-900">
              First Name
            </label>
            <div className="mt-2">
              <input
                id="firstName"
                name="firstName"
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 
                  shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 
                  focus:ring-2 focus:ring-inset focus:ring-yellow-600 sm:text-sm"
              />
            </div>
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-900">
              Last Name
            </label>
            <div className="mt-2">
              <input
                id="lastName"
                name="lastName"
                type="text"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 
                  shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 
                  focus:ring-2 focus:ring-inset focus:ring-yellow-600 sm:text-sm"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-900">
              Email address
            </label>
            <div className="mt-2">
              <input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={handleEmailBlur}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 
                  shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 
                  focus:ring-2 focus:ring-inset focus:ring-yellow-600 sm:text-sm"
              />
              {emailError && <p className="text-red-500 text-sm mt-2">{emailError}</p>}
            </div>
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-900">
              Password
            </label>
            <div className="mt-2">
              <input
                id="password"
                name="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 
                  shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 
                  focus:ring-2 focus:ring-inset focus:ring-yellow-600 sm:text-sm"
              />
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-900">
              Confirm Password
            </label>
            <div className="mt-2">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 
                  shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 
                  focus:ring-2 focus:ring-inset focus:ring-yellow-600 sm:text-sm"
              />
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
          {success && <p className="text-green-500 text-sm mt-2">{success}</p>}

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              className={`flex w-full justify-center rounded-md px-3 py-1.5 text-sm 
                font-semibold text-white shadow-sm focus-visible:outline 
                focus-visible:outline-2 focus-visible:outline-offset-2 ${
                formIsValid
                  ? 'bg-yellow-600 hover:bg-yellow-500 focus-visible:outline-yellow-600'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
              disabled={!formIsValid}
            >
              Create Account
            </button>
          </div>
        </form>

        <p className="mt-10 text-center text-sm text-gray-500">
          Already have an account?{' '}
          <a href="/login" className="font-semibold text-yellow-600 hover:text-yellow-500">
            Sign in
          </a>
        </p>
        <p className="mt-3 text-center text-sm text-gray-500">
          Didn't mean to be here?{' '}
          <a href="/" className="font-semibold text-yellow-600 hover:text-yellow-500">
            Back to home
          </a>
        </p>
      </div>
    </div>
  );
}
